import { useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AdminCard from "./AdminCard";

const SES_REGIONS = [
    ["eu-central-1", "Frankfurt (eu-central-1)"],
    ["eu-west-1", "Irlandia (eu-west-1)"],
    ["eu-north-1", "Sztokholm (eu-north-1)"],
    ["eu-west-3", "Paryż (eu-west-3)"],
    ["us-east-1", "N. Virginia (us-east-1)"],
];

const isMasked = (v) => !!v && (v.includes("…") || v.includes("•••"));

const SecretInput = ({ value, onChange, placeholder, testid }) => {
    const saved = useRef("");
    if (isMasked(value)) saved.current = value;
    // Clearing the field restores the saved mask, so a stored secret is never wiped by accident.
    const handle = (e) => onChange(e.target.value === "" && saved.current ? { target: { value: saved.current } } : e);
    return (
        <Input type="password" value={isMasked(value) ? "" : value || ""} onChange={handle} autoComplete="new-password" data-testid={testid}
            placeholder={saved.current ? `zapisany (${saved.current}) – wpisz nowy, aby zmienić` : placeholder} />
    );
};

const ProviderToggle = ({ value, onChange }) => (
    <div className="inline-flex p-1 rounded-full bg-[#FAF8F3] border border-gray-200" role="radiogroup" data-testid="admin-settings-mail-provider">
        {[["resend", "Resend"], ["ses", "Amazon SES"]].map(([k, l]) => (
            <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`admin-settings-mail-provider-${k}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${value === k ? "bg-[#0D4F3C] text-white shadow" : "text-[#092E22] hover:bg-white"}`}>
                {l}
            </button>
        ))}
    </div>
);

const SesFields = ({ form, setField }) => (
    <>
        <div className="md:col-span-2 text-xs bg-sky-50 border border-sky-200 text-sky-900 rounded-xl px-3 py-2">
            Wymagane: domena zweryfikowana w <strong>SES → Identities</strong> (rekordy DKIM w DNS) w wybranym regionie oraz wyjście z trybu sandbox
            („Request production access”). W sandboxie SES wysyła tylko na zweryfikowane adresy. Użytkownik IAM potrzebuje tylko uprawnienia <code>ses:SendEmail</code>.
        </div>
        <div className="space-y-2">
            <Label>Access Key ID</Label>
            <Input value={form.ses_access_key_id || ""} onChange={setField("ses_access_key_id")} placeholder="AKIA…" autoComplete="off" data-testid="admin-settings-ses-key-id" />
        </div>
        <div className="space-y-2">
            <Label>Secret Access Key</Label>
            <SecretInput value={form.ses_secret_access_key} onChange={setField("ses_secret_access_key")} placeholder="wklej Secret Access Key" testid="admin-settings-ses-secret" />
            <p className="text-[11px] text-gray-500">Sekret nie jest nigdy odsyłany do przeglądarki – widzisz tylko maskę.</p>
        </div>
        <div className="space-y-2">
            <Label>Region AWS</Label>
            <select value={form.ses_region || "eu-central-1"} onChange={setField("ses_region")} data-testid="admin-settings-ses-region"
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                {SES_REGIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
        </div>
        <div className="space-y-2">
            <Label>Configuration Set (opcjonalnie)</Label>
            <Input value={form.ses_configuration_set || ""} onChange={setField("ses_configuration_set")} placeholder="np. aluzyjna-tracking" data-testid="admin-settings-ses-config-set" />
        </div>
    </>
);

export const EmailProviderCard = ({ form, setField, setForm, save, loading, testButton }) => {
    const provider = form.mail_provider || "resend";
    const isSes = provider === "ses";
    return (
        <AdminCard className="lg:col-span-2">
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
                <div>
                    <h3 className="font-serif text-xl text-[#092E22] mb-1">Wysyłka e-mail</h3>
                    <p className="text-sm text-gray-500 max-w-2xl">Dostawca dla wszystkich wiadomości: mailing, potwierdzenia i linki śledzenia zgłoszeń, formularz kontaktowy, powiadomienia wykonawców. Dane drugiego dostawcy zostają zapisane jako zapas.</p>
                </div>
                <ProviderToggle value={provider} onChange={(v) => setForm((f) => ({ ...f, mail_provider: v }))} />
            </div>
            <form onSubmit={save} className="grid md:grid-cols-2 gap-4" data-testid="admin-settings-resend-form">
                {isSes ? <SesFields form={form} setField={setField} /> : (
                    <div className="md:col-span-2 space-y-2">
                        <Label>Resend API Key (re_...)</Label>
                        <SecretInput value={form.resend_api_key} onChange={setField("resend_api_key")} placeholder="re_xxxxxxxxxxxxxxxxxxxxxxxx" testid="admin-settings-resend-key" />
                        <p className="text-[11px] text-gray-500">Klucz z <a href="https://resend.com" target="_blank" rel="noreferrer" className="underline">resend.com</a>; nie jest odsyłany do przeglądarki.</p>
                    </div>
                )}
                <div className="space-y-2">
                    <Label>Adres nadawcy (zweryfikowana domena)</Label>
                    <Input type="email" value={form.resend_from_email} onChange={setField("resend_from_email")} placeholder="noreply@aluzyjna25cdef.pl" data-testid="admin-settings-resend-from-email" />
                </div>
                <div className="space-y-2">
                    <Label>Nazwa nadawcy</Label>
                    <Input value={form.resend_from_name} onChange={setField("resend_from_name")} placeholder="Dąbrówka Wiślana" data-testid="admin-settings-resend-from-name" />
                </div>
                <p className="md:col-span-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    Adres nadawcy musi używać <strong>Twojej domeny</strong> zweryfikowanej u wybranego dostawcy – gmail.com, onet.pl, wp.pl nie zadziałają.
                </p>
                <div className="md:col-span-2 flex flex-wrap items-center gap-3">
                    <Button type="submit" disabled={loading} data-testid="admin-settings-resend-save" className="bg-[#0D4F3C] hover:bg-[#092E22] text-white rounded-full px-6">
                        {loading ? "Zapisywanie..." : `Zapisz (aktywny: ${isSes ? "Amazon SES" : "Resend"})`}
                    </Button>
                    {testButton}
                </div>
            </form>
        </AdminCard>
    );
};

export default EmailProviderCard;
